import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PolicyApi } from '@Apis';
import { OverAllPolicyType, ServiceCollection } from '../../utils/types';
import { createPolicyQueryKey } from './usePolicyListQuery';
import { updatePolicyPriorityInCache } from '../utils/policyCacheUpdaters';
import {
  createPolicyMutationKey,
  PolicyMutationKey,
} from '../context/PolicyMutationContext';

interface UsePolicyReorderMutationParams {
  overAllPolicyType: OverAllPolicyType;
  collectionType: ServiceCollection;
  orgId: string;
}

interface ReorderMutationVariables {
  policyId: string;
  newPriority: number;
  oldIndex: number;
  targetIndex: number;
}

export const usePolicyReorderMutation = ({
  overAllPolicyType,
  collectionType,
  orgId,
}: UsePolicyReorderMutationParams) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = createPolicyQueryKey(
    overAllPolicyType,
    collectionType,
    orgId
  );

  const [reorderingPolicyTypes, setReorderingPolicyTypes] = useState<
    Set<PolicyMutationKey>
  >(new Set());

  const mutation = useMutation({
    mutationFn: ({ policyId, newPriority }: ReorderMutationVariables) =>
      PolicyApi.apiUpdatePolicyPriority(policyId, newPriority),

    onMutate: async ({ policyId, newPriority }: ReorderMutationVariables) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData(queryKey);

      // Get the policy from the cache and create mutation key
      const cacheData = previousData as any;
      const policy = cacheData?.data?.find(
        (p: any) => p.policyUuid === policyId
      );
      const mutationKey: PolicyMutationKey | undefined =
        policy?.policyType !== undefined
          ? createPolicyMutationKey(
              policy.policyType,
              policy.policySubType,
              overAllPolicyType,
              collectionType,
              orgId
            )
          : undefined;

      if (mutationKey) {
        setReorderingPolicyTypes((prev) => new Set(prev).add(mutationKey));
      }

      queryClient.setQueryData(queryKey, (oldData: any) =>
        updatePolicyPriorityInCache(oldData, policyId, newPriority)
      );

      return { previousData, mutationKey };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    },

    onSettled: (_data, _error, _variables, context) => {
      // Remove mutation key from reordering set
      const keyToRemove = context?.mutationKey;
      if (keyToRemove) {
        setReorderingPolicyTypes((prev) => {
          const next = new Set(prev);
          next.delete(keyToRemove);
          return next;
        });
      }

      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    ...mutation,
    reorderingPolicyTypes,
  };
};
